import { useMemo, useState } from 'react';
import * as Plot from '@observablehq/plot';
import PlotFigure from '../../PlotFigure';
import ChartOverlay from '../common/ChartOverlay';

const sectionStyle = {
    marginTop: '24px',
    display: 'grid',
    gap: '24px',
    gridTemplateColumns: 'minmax(0, 1fr)',
};

const parseTimestamp = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const numeric = typeof value === 'string' ? Number(value) : value;

    if (Number.isFinite(numeric)) {
        const millis = numeric > 1e12 ? numeric : numeric * 1000;
        const dt = new Date(millis);
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    if (typeof value === 'string') {
        const dt = new Date(value);
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    return null;
};

const normaliseRestartEvents = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    return items
        .map((item) => {
            const when =
                parseTimestamp(item?.timestamp) ||
                parseTimestamp(item?.ts) ||
                parseTimestamp(item?.report_time) ||
                parseTimestamp(item?.created_at) ||
                parseTimestamp(item?.updated_at);

            if (!when) {
                return null;
            }

            const reason = item?.reset_reason || item?.reason || 'Restart';
            const firmware = item?.firmware || item?.firmware_version;
            return {
                when,
                reason: String(reason),
                firmware: firmware ? String(firmware) : null,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.when.getTime() - b.when.getTime());
};

const groupByHour = (events) => {
    const buckets = new Map();

    events.forEach((event) => {
        const bucket = new Date(event.when.getFullYear(), event.when.getMonth(), event.when.getDate(), event.when.getHours());
        const key = `${event.reason}-${bucket.toISOString()}`;
        const entry = buckets.get(key) || { reason: event.reason, time: bucket, count: 0 };
        entry.count += 1;
        buckets.set(key, entry);
    });

    return Array.from(buckets.values()).sort((a, b) => a.time.getTime() - b.time.getTime());
};

function RestartsChart({ items }) {
    const events = useMemo(() => normaliseRestartEvents(items), [items]);
    const aggregated = useMemo(() => groupByHour(events), [events]);
    const [expanded, setExpanded] = useState(false);
    const [timelineExpanded, setTimelineExpanded] = useState(false);

    const reasonDomain = useMemo(
        () => Array.from(new Set(events.map((event) => event.reason || 'Restart'))),
        [events]
    );

    const recent = useMemo(() => events.slice(-6).reverse(), [events]);

    const hourlyPlot = useMemo(
        () => ({
            x: { type: 'time', grid: true, label: 'Timestamp' },
            y: { grid: true, label: 'Restarts per hour' },
            color: {
                legend: true,
                type: 'ordinal',
                domain: reasonDomain,
                range: [
                    '#4df7ff',
                    '#ffd93d',
                    '#8df0a1',
                    '#ff6bd6',
                    '#cfa4ff',
                    '#ff9f68',
                ].slice(0, Math.max(1, reasonDomain.length)),
            },
            marks: [
                Plot.ruleY([0]),
                Plot.line(aggregated, { x: 'time', y: 'count', stroke: 'reason' }),
                Plot.dot(aggregated, {
                    x: 'time',
                    y: 'count',
                    fill: 'reason',
                    title: (d) => `${d.count} restart${d.count === 1 ? '' : 's'}\n${d.time.toLocaleString()}\nReason: ${d.reason}`,
                }),
                Plot.tip(
                    aggregated,
                    Plot.pointerX({
                        x: 'time',
                        y: 'count',
                        stroke: 'reason',
                        title: (d) => `${d.count} restart${d.count === 1 ? '' : 's'}\n${d.time.toLocaleString()}\nReason: ${d.reason}`,
                        pointer: 'mouse',
                        className: 'plot-tip neon-plot-tip',
                        textPadding: 12,
                        pointerSize: 14,
                        lineHeight: 1.6,
                        fontSize: 12,
                        fontWeight: 600,
                    })
                ),
            ],
        }),
        [aggregated, reasonDomain]
    );

    const timelinePlot = useMemo(
        () => ({
            x: { type: 'time', grid: true },
            y: { type: 'band', tickFormat: () => '' },
            color: {
                legend: true,
                type: 'ordinal',
                domain: reasonDomain,
                range: [
                    '#4df7ff',
                    '#ffd93d',
                    '#8df0a1',
                    '#ff6bd6',
                    '#cfa4ff',
                    '#ff9f68',
                ].slice(0, Math.max(1, reasonDomain.length)),
            },
            marks: [
                Plot.dot(events, {
                    x: 'when',
                    y: 'reason',
                    fill: 'reason',
                    r: 6.5,
                    title: (d) => `${d.reason}\n${d.when.toLocaleString()}${d.firmware ? `\nFirmware: ${d.firmware}` : ''}`,
                }),
            ],
        }),
        [events, reasonDomain]
    );

    if (!events.length) {
        return <p>No restarts recorded for the selected interval.</p>;
    }

    return (
        <div style={sectionStyle}>
            <section className="plot-section restarts-chart">
                <header className="plot-section__header">
                    <div className="plot-section__header-text">
                        <h3>Restart volume by reason</h3>
                        <p className="plot-description">Aggregated restart counts grouped by reported reason within the selected window.</p>
                    </div>
                    <button type="button" className="plot-expand-button" onClick={() => setExpanded(true)}>
                        ⤢ Expand
                    </button>
                </header>
                <PlotFigure options={hourlyPlot} />
            </section>

            <section className="plot-section restarts-timeline">
                <header className="plot-section__header">
                    <div className="plot-section__header-text">
                        <h3>Restart timeline</h3>
                        <p className="plot-description">Individual restart events plotted over time with the associated reason.</p>
                    </div>
                    <button type="button" className="plot-expand-button" onClick={() => setTimelineExpanded(true)}>
                        ⤢ Expand
                    </button>
                </header>
                <PlotFigure options={timelinePlot} height={320} />
            </section>

            {recent.length ? (
                <aside className="alerts-summary">
                    <h4>Most recent restarts</h4>
                    <ul>
                        {recent.map((event, index) => (
                            <li key={`${event.when.getTime()}-${index}`}>
                                <div className="alerts-summary__code">{event.reason}</div>
                                {event.firmware ? (
                                    <div className="alerts-summary__message">Firmware: {event.firmware}</div>
                                ) : null}
                                <div className="alerts-summary__timestamp">{event.when.toLocaleString()}</div>
                            </li>
                        ))}
                    </ul>
                </aside>
            ) : null}

            {expanded ? (
                <ChartOverlay
                    title="Restart volume by reason"
                    description="Aggregated restart counts grouped by reason within the selected range."
                    onClose={() => setExpanded(false)}
                >
                    <PlotFigure options={hourlyPlot} height="min(72vh, 680px)" className="chart-overlay__plot" />
                </ChartOverlay>
            ) : null}

            {timelineExpanded ? (
                <ChartOverlay
                    title="Restart timeline"
                    description="Individual restarts plotted over time. Hover for firmware details."
                    onClose={() => setTimelineExpanded(false)}
                >
                    <PlotFigure options={timelinePlot} height="min(72vh, 640px)" className="chart-overlay__plot" />
                </ChartOverlay>
            ) : null}
        </div>
    );
}

export default RestartsChart;
